import { useState, useEffect, useCallback } from 'react';
import apiClient from '../lib/api';
import { useProject } from '../lib/project-context';
import { RequiresProject } from '../components/RequiresContext';
import { ModuleHeader } from '../components/ModuleHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  HardHat, AlertTriangle, CheckCircle2, TrendingUp, DollarSign,
  Plus, Loader2, BarChart3, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

function fmt(n) { return n != null ? new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n) : '-'; }
function fmtD(n, d = 1) { return n != null ? Number(n).toFixed(d) : '-'; }

export default function ConstructionIntelligencePage() {
  const { activeProject, activeScenario, projects } = useProject();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingCost, setAddingCost] = useState(false);
  const [addingProgress, setAddingProgress] = useState(false);
  const [costForm, setCostForm] = useState({ month: 1, category: 'hard_cost', amount: '', description: '' });
  const [progressForm, setProgressForm] = useState({ month: 1, construction_pct: 0, notes: '' });

  const pid = activeProject?.id;
  const sid = activeScenario?.id;

  const loadData = useCallback(async () => {
    if (!pid || !sid) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await apiClient.get(`/construction/dashboard/${pid}?scenario_id=${sid}`);
      setDashboard(res.data);
    } catch (err) { console.warn('[ConstructionIntelligence] initial fetch failed', err); }
    setLoading(false);
  }, [pid, sid]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddCost = async () => {
    if (!costForm.amount) return;
    try {
      await apiClient.post('/construction/budget-actual', { project_id: pid, scenario_id: sid, ...costForm, amount: parseFloat(costForm.amount) });
      toast.success('Costo registrado');
      setCostForm({ month: costForm.month, category: 'hard_cost', amount: '', description: '' });
      setAddingCost(false);
      loadData();
    } catch { toast.error('Error'); }
  };

  const handleAddProgress = async () => {
    try {
      await apiClient.post('/construction/progress', { project_id: pid, scenario_id: sid, ...progressForm });
      toast.success('Avance registrado');
      setAddingProgress(false);
      loadData();
    } catch { toast.error('Error'); }
  };

  if (!projects?.length) return <RequiresProject moduleName="Construction Intelligence"><div /></RequiresProject>;
  if (!pid) return <EmptyMsg icon={<HardHat />} title="Selecciona un proyecto" sub="Elige un proyecto desde el Dashboard." />;
  if (!sid) return <EmptyMsg icon={<Target />} title="Selecciona un escenario" sub="Se requiere un escenario con análisis ejecutado." />;

  const d = dashboard;
  const s = d?.summary;
  const alerts = d?.alerts || [];
  const comparison = d?.budget_comparison || [];

  return (
    <div className="h-full overflow-y-auto" data-testid="construction-page">
      <div className="px-6 lg:px-8 py-6 max-w-[1400px]">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header with project/scenario selectors */}
          <ModuleHeader title="Construction Intelligence" subtitle="Monitoreo y control de avance de obra">
            <Button variant="outline" size="sm" onClick={() => setAddingCost(!addingCost)} data-testid="add-cost-btn">
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Registrar costo
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAddingProgress(!addingProgress)} data-testid="add-progress-btn">
              <TrendingUp className="w-3.5 h-3.5 mr-1" /> Registrar avance
            </Button>
          </ModuleHeader>

          {loading ? (
            <div className="space-y-4">{[1,2].map(i => <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: 'var(--bg-hover)' }} />)}</div>
          ) : !d?.has_data ? (
            <Card className="rounded-xl"><CardContent className="py-12 text-center">
              <HardHat className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--construction-primary)', opacity: 0.3 }} />
              <p className="text-sm font-medium mb-1">Sin análisis financiero</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Ejecuta un análisis en Financial Intelligence para ver el presupuesto planificado.</p>
            </CardContent></Card>
          ) : (
            <>
              {/* Add cost form */}
              {addingCost && (
                <Card className="rounded-xl border-border/50 shadow-sm mb-4" data-testid="cost-form">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold mb-3">Registrar costo real</h4>
                    <div className="flex gap-3 items-end flex-wrap">
                      <div className="space-y-1 w-20"><Label className="text-[10px]">Mes</Label><Input type="number" min={1} value={costForm.month} onChange={e => setCostForm(p => ({ ...p, month: parseInt(e.target.value) || 1 }))} className="h-9 text-right" /></div>
                      <div className="space-y-1 w-36"><Label className="text-[10px]">Categoría</Label>
                        <Select value={costForm.category} onValueChange={v => setCostForm(p => ({ ...p, category: v }))}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hard_cost">Costo duro</SelectItem>
                            <SelectItem value="soft_cost">Costo blando</SelectItem>
                            <SelectItem value="indirect">Indirecto</SelectItem>
                            <SelectItem value="land">Terreno</SelectItem>
                            <SelectItem value="contingency">Contingencia</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1 w-32"><Label className="text-[10px]">Monto</Label><Input type="number" value={costForm.amount} onChange={e => setCostForm(p => ({ ...p, amount: e.target.value }))} className="h-9 text-right" placeholder="$0" /></div>
                      <div className="space-y-1 flex-1 min-w-[120px]"><Label className="text-[10px]">Descripción</Label><Input value={costForm.description} onChange={e => setCostForm(p => ({ ...p, description: e.target.value }))} className="h-9" placeholder="Opcional" /></div>
                      <Button onClick={handleAddCost} className="h-9 text-white" style={{ background: 'var(--construction-primary)' }}>Guardar</Button>
                      <Button variant="ghost" onClick={() => setAddingCost(false)} className="h-9">Cancelar</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Add progress form */}
              {addingProgress && (
                <Card className="rounded-xl border-border/50 shadow-sm mb-4" data-testid="progress-form">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold mb-3">Registrar avance de obra</h4>
                    <div className="flex gap-3 items-end flex-wrap">
                      <div className="space-y-1 w-20"><Label className="text-[10px]">Mes</Label><Input type="number" min={1} value={progressForm.month} onChange={e => setProgressForm(p => ({ ...p, month: parseInt(e.target.value) || 1 }))} className="h-9 text-right" /></div>
                      <div className="space-y-1 w-32"><Label className="text-[10px]">Avance (%)</Label><Input type="number" min={0} max={100} value={progressForm.construction_pct} onChange={e => setProgressForm(p => ({ ...p, construction_pct: parseFloat(e.target.value) || 0 }))} className="h-9 text-right" /></div>
                      <div className="space-y-1 flex-1 min-w-[120px]"><Label className="text-[10px]">Notas</Label><Input value={progressForm.notes} onChange={e => setProgressForm(p => ({ ...p, notes: e.target.value }))} className="h-9" placeholder="Opcional" /></div>
                      <Button onClick={handleAddProgress} className="h-9 text-white" style={{ background: 'var(--construction-primary)' }}>Guardar</Button>
                      <Button variant="ghost" onClick={() => setAddingProgress(false)} className="h-9">Cancelar</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left */}
                <div className="lg:col-span-8 space-y-6">
                  {/* KPIs */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KPI label="Presupuesto" value={`$${fmt(s.planned_total)}`} />
                    <KPI label="Gasto real" value={`$${fmt(s.actual_total)}`} color={s.variance > 0 ? 'var(--danger)' : 'var(--success)'} />
                    <KPI label="Variación" value={`${s.variance_pct > 0 ? '+' : ''}${fmtD(s.variance_pct)}%`} sub={`$${fmt(Math.abs(s.variance))}`} color={Math.abs(s.variance_pct) > 5 ? 'var(--warning-color)' : 'var(--success)'} />
                    <KPI label="Avance de obra" value={`${fmtD(s.construction_pct, 0)}%`} sub={`Mes ${s.current_month} de ${s.total_months}`} />
                  </div>

                  {/* Budget comparison table */}
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Presupuesto vs Real</h3>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/20">
                            <TableHead className="text-xs">Categoría</TableHead>
                            <TableHead className="text-xs text-right">Presupuesto</TableHead>
                            <TableHead className="text-xs text-right">Real</TableHead>
                            <TableHead className="text-xs text-right">Variación</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparison.map(b => (
                            <TableRow key={b.key}>
                              <TableCell className="text-sm font-medium">{b.category}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">${fmt(b.planned)}</TableCell>
                              <TableCell className="text-sm text-right tabular-nums">{b.actual > 0 ? `$${fmt(b.actual)}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</TableCell>
                              <TableCell className="text-right">
                                {b.actual > 0 ? (
                                  <Badge className="text-[10px] tabular-nums" style={{ background: Math.abs(b.variance_pct) > 10 ? 'var(--danger-soft)' : b.variance_pct > 0 ? 'var(--warning-soft)' : 'var(--success-soft)', color: Math.abs(b.variance_pct) > 10 ? 'var(--danger)' : b.variance_pct > 0 ? 'var(--warning-color)' : 'var(--success)', border: 'none' }}>
                                    {b.variance_pct > 0 ? '+' : ''}{fmtD(b.variance_pct)}%
                                  </Badge>
                                ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Progress timeline */}
                  {(d.progress || []).length > 0 && (
                    <Card className="rounded-xl border-border/50 shadow-sm">
                      <CardContent className="p-5">
                        <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Historial de Avance</h3>
                        <div className="space-y-2">
                          {d.progress.map((p, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                              <span className="text-xs font-semibold w-14 shrink-0" style={{ color: 'var(--text-muted)' }}>Mes {p.month}</span>
                              <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--bg-hover)' }}>
                                <div className="h-full rounded-full" style={{ width: `${p.construction_pct}%`, background: 'var(--construction-primary)' }} />
                              </div>
                              <span className="text-xs font-bold tabular-nums w-10 text-right">{fmtD(p.construction_pct, 0)}%</span>
                              {p.notes && <span className="text-[10px] truncate max-w-[150px]" style={{ color: 'var(--text-muted)' }}>{p.notes}</span>}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Right — Alerts */}
                <div className="lg:col-span-4 space-y-3">
                  <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Alertas</h3>
                  {alerts.length > 0 ? alerts.map((a, i) => (
                    <Card key={i} className="rounded-xl border-border/50 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: a.severity === 'high' ? 'var(--danger)' : 'var(--warning-color)' }} />
                          <div>
                            <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{a.title}</div>
                            <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{a.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )) : (
                    <Card className="rounded-xl border-border/50"><CardContent className="p-4 text-center">
                      <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--success)', opacity: 0.4 }} />
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Sin alertas. Registra costos reales para comparar.</p>
                    </CardContent></Card>
                  )}
                  {s?.actual_total === 0 && (
                    <div className="text-center p-4 rounded-lg" style={{ border: '1px dashed var(--border-default)' }}>
                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Registra el primer costo real para comenzar a comparar presupuesto vs real.</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, color }) {
  return (
    <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-lg font-display font-bold tabular-nums" style={{ color: color || 'var(--text-primary)' }}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

function EmptyMsg({ icon, title, sub }) {
  return <div className="px-6 py-20 text-center"><div className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--construction-primary)', opacity: 0.3 }}>{icon}</div><h2 className="font-display text-lg font-semibold mb-1">{title}</h2><p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sub}</p></div>;
}
